import { cn } from '@/utils/cn';

export interface SimpleDonutChartProps {
  data: Array<{ label: string; value: number; color?: string }>;
  formatValue?: (value: number) => string;
  size?: number;
  className?: string;
  /** Center label e.g. total */
  centerLabel?: string;
  centerValue?: string;
}

/** Cycles brand / semantic colours, then muted mixes for longer series. */
const CHART_PALETTE = [
  'var(--brand-primary)',
  'var(--semantic-success)',
  'var(--semantic-warning)',
  'var(--semantic-info)',
  'var(--semantic-danger)',
  'var(--brand-accent)',
  'color-mix(in oklch, var(--brand-primary) 50%, var(--text-tertiary))',
  'color-mix(in oklch, var(--semantic-success) 50%, var(--text-tertiary))',
  'color-mix(in oklch, var(--semantic-warning) 50%, var(--text-tertiary))',
  'color-mix(in oklch, var(--semantic-info) 50%, var(--text-tertiary))',
  'color-mix(in oklch, var(--semantic-danger) 50%, var(--text-tertiary))',
] as const;

const polar = (cx: number, cy: number, r: number, angle: number) => {
  const rad = ((angle - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
};

const describeArc = (
  cx: number,
  cy: number,
  outerR: number,
  innerR: number,
  startAngle: number,
  endAngle: number,
) => {
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  const oStart = polar(cx, cy, outerR, startAngle);
  const oEnd = polar(cx, cy, outerR, endAngle);
  const iEnd = polar(cx, cy, innerR, endAngle);
  const iStart = polar(cx, cy, innerR, startAngle);

  return [
    `M ${oStart.x} ${oStart.y}`,
    `A ${outerR} ${outerR} 0 ${largeArc} 1 ${oEnd.x} ${oEnd.y}`,
    `L ${iEnd.x} ${iEnd.y}`,
    `A ${innerR} ${innerR} 0 ${largeArc} 0 ${iStart.x} ${iStart.y}`,
    'Z',
  ].join(' ');
};

/**
 * Lightweight SVG donut for category breakdowns — no chart library.
 *
 * Legend items pack label and value together (not stretched edge-to-edge) so
 * counts stay next to their category inside the card padding.
 */
export const SimpleDonutChart = ({
  data,
  formatValue = String,
  size = 160,
  className,
  centerLabel,
  centerValue,
}: SimpleDonutChartProps) => {
  const slices = data
    .filter((d) => d.value > 0)
    .map((d, index) => ({
      ...d,
      color: d.color ?? CHART_PALETTE[index % CHART_PALETTE.length]!,
    }));

  const total = slices.reduce((sum, d) => sum + d.value, 0);
  const resolvedCenterValue = centerValue ?? (total > 0 ? formatValue(total) : undefined);

  if (slices.length === 0 || total <= 0) {
    return (
      <div
        className={cn(
          'flex items-center justify-center text-body-sm text-fg-secondary',
          className,
        )}
        style={{ minHeight: size }}
      />
    );
  }

  const cx = size / 2;
  const cy = size / 2;
  const outerR = size * 0.42;
  const innerR = size * 0.26;
  let cursor = 0;

  const arcs = slices.map((slice) => {
    const sweep = (slice.value / total) * 360;
    const startAngle = cursor;
    const endAngle = cursor + Math.min(sweep, 359.999);
    cursor += sweep;
    return { ...slice, startAngle, endAngle };
  });

  return (
    <div
      className={cn(
        'flex w-full min-w-0 flex-col items-center gap-4 sm:flex-row sm:items-center sm:justify-start sm:gap-5',
        className,
      )}
    >
      <svg
        role="img"
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="shrink-0"
        aria-label={
          resolvedCenterValue
            ? `${centerLabel ?? ''} ${resolvedCenterValue}`.trim()
            : undefined
        }
      >
        {arcs.length === 1 ? (
          <>
            <circle
              cx={cx}
              cy={cy}
              r={outerR}
              fill={arcs[0]!.color}
              role="img"
              aria-label={`${arcs[0]!.label}: ${formatValue(arcs[0]!.value)}`}
            >
              <title>{`${arcs[0]!.label}: ${formatValue(arcs[0]!.value)}`}</title>
            </circle>
            <circle cx={cx} cy={cy} r={innerR} fill="var(--surface)" />
          </>
        ) : (
          arcs.map((arc, index) => {
            const formatted = formatValue(arc.value);
            const aria = `${arc.label}: ${formatted}`;
            return (
              <path
                key={`${arc.label}-${index}`}
                d={describeArc(cx, cy, outerR, innerR, arc.startAngle, arc.endAngle)}
                fill={arc.color}
                role="img"
                aria-label={aria}
              >
                <title>{aria}</title>
              </path>
            );
          })
        )}

        {(resolvedCenterValue || centerLabel) && (
          <g>
            {resolvedCenterValue ? (
              <text
                x={cx}
                y={centerLabel ? cy - 4 : cy}
                textAnchor="middle"
                dominantBaseline={centerLabel ? 'auto' : 'middle'}
                className="fill-[var(--text-primary)]"
                style={{ fontSize: size * 0.12, fontWeight: 600 }}
              >
                {resolvedCenterValue}
              </text>
            ) : null}
            {centerLabel ? (
              <text
                x={cx}
                y={resolvedCenterValue ? cy + 14 : cy + 4}
                textAnchor="middle"
                className="fill-[var(--text-tertiary)]"
                style={{ fontSize: size * 0.06 }}
              >
                {centerLabel.length > 16 ? `${centerLabel.slice(0, 15)}…` : centerLabel}
              </text>
            ) : null}
          </g>
        )}
      </svg>

      <ul className="m-0 flex w-full min-w-0 list-none flex-col gap-2 p-0 sm:max-w-[55%] sm:flex-1">
        {arcs.map((arc, index) => {
          const formatted = formatValue(arc.value);
          return (
            <li
              key={`${arc.label}-${index}`}
              className="flex min-w-0 items-center gap-2 text-caption"
            >
              <span
                aria-hidden
                className="size-2.5 shrink-0 rounded-sm"
                style={{ backgroundColor: arc.color }}
              />
              <span className="min-w-0 truncate text-fg-secondary" title={arc.label}>
                {arc.label}
              </span>
              <span className="shrink-0 tabular-nums text-fg">{formatted}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
